/**
 * Cost Management embedded panel — shared between EntraGovernance and (later)
 * AWS Governance. Driven by a `provider` prop ('entra' or 'aws') that picks
 * the right API base.
 *
 * For Entra, this points to /finops/* endpoints (Azure Cost Management).
 * For AWS, this points to /aws/cost/* endpoints (Cost Explorer).
 */
import { useEffect, useState } from 'react';
import {
  LayoutDashboard, Server, FileBarChart, Settings, RefreshCw,
  TrendingUp, AlertTriangle, Download, FileText, FileSpreadsheet, Cloud, Wallet,
  ShieldCheck, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const SUB_TABS = [
  { key: 'dashboard',  label: 'Dashboard',         Icon: LayoutDashboard },
  { key: 'resources',  label: 'Resources',         Icon: Server },
  { key: 'reports',    label: 'Reports',           Icon: FileBarChart },
  { key: 'settings',   label: 'Settings',          Icon: Settings },
];

// Endpoint map per provider
const ENDPOINTS = {
  entra: {
    settings: '/finops/settings',
    dashboard: '/finops/dashboard',
    resources: '/finops/resources',
    subscriptions: '/finops/subscriptions',
    testConn: '/finops/test-connection',
    reportsList: '/finops/reports/list',
    reportPath: (kind, fmt, token) => `/api/finops/reports/${kind}?format=${fmt}&auth=${token}`,
    labels: {
      pickerLabel: 'Subscription IDs',
      pickerHint: 'Add the Azure Subscription ID(s) you want to track. The Entra App registration must be granted Cost Management Reader (or Reader) role at each subscription scope.',
      addPlaceholder: '00000000-0000-0000-0000-000000000000',
    },
    discoverField: 'subscription_ids',
    discoverItemId: (d) => d.id,
    discoverItemLabel: (d) => `${d.name} · ${d.id?.slice(0, 8)}…`,
  },
  aws: {
    settings: '/aws/cost/settings',   // not used — AWS shares /aws/settings
    dashboard: '/aws/cost/dashboard',
    resources: '/aws/cost/resources',
    subscriptions: '/aws/accounts',
    testConn: '/aws/test-connection',
    reportsList: '/aws/reports/list',
    reportPath: (kind, fmt, token) => `/api/aws/reports/${kind}?format=${fmt}&auth=${token}`,
    labels: {
      pickerLabel: 'Linked Accounts',
      pickerHint: 'AWS Cost Explorer aggregates across all linked accounts in your Organization. Enable Organization mode in the AWS Settings panel above to see per-account breakdown.',
      addPlaceholder: '',
    },
    discoverField: 'accounts',
    discoverItemId: (d) => d.id,
    discoverItemLabel: (d) => `${d.name || d.id} · ${d.id}`,
  },
};

export default function CostManagementPanel({ provider = 'entra' }) {
  const cfg = ENDPOINTS[provider] || ENDPOINTS.entra;
  const [tab, setTab] = useState('dashboard');
  const [bump, setBump] = useState(0);
  const refresh = () => setBump((b) => b + 1);

  return (
    <div className="space-y-4" data-testid={`cost-mgmt-panel-${provider}`}>
      <div className="flex flex-wrap gap-1 border-b border-slate-200 overflow-x-auto bg-slate-50/50 rounded-t-2xl px-2">
        {SUB_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`cost-tab-${provider}-${t.key}`}
            className={`px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === t.key ? 'border-[#3D4A8E] text-[#3D4A8E]' : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            <t.Icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard'  && <DashboardTab cfg={cfg} provider={provider} bump={bump} />}
      {tab === 'resources'  && <ResourcesTab cfg={cfg} provider={provider} bump={bump} />}
      {tab === 'reports'    && <ReportsTab   cfg={cfg} provider={provider} />}
      {tab === 'settings'   && <SettingsTab  cfg={cfg} provider={provider} onSaved={refresh} />}
    </div>
  );
}

// --------------------------------------------------------------------------
function DashboardTab({ cfg, provider, bump }) {
  const [d, setD] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(cfg.dashboard); setD(data); }
      catch (e) {
        const detail = e?.response?.data?.detail || 'Failed to load dashboard';
        setD({ configured: false, message: detail, throttled: detail.toLowerCase().includes('throttl') });
      }
    })();
  }, [bump, cfg.dashboard]);

  const forceRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get(`${cfg.dashboard}?force=true`);
      setD(data);
      toast.success('Cost data refreshed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  if (d === null) return <div className="text-sm text-slate-500">Loading cost data…</div>;
  if (!d?.configured) return <EmptyState message={d?.message || 'No data'} provider={provider} throttled={d?.throttled} />;

  const cur = d.currency || 'USD';
  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: cur, maximumFractionDigits: 2 }).format(n || 0);
  // INR companion (AWS only — exchange rate fetched daily server-side)
  const inrRate = (provider === 'aws' && d.inr_rate) ? d.inr_rate : 0;
  const inr = (n) => inrRate ? `₹${(Number(n || 0) * inrRate).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '';
  const usdInr = (n) => inrRate ? <>{fmt(n)}<span className="block text-sm font-bold text-emerald-700 mt-0.5">{inr(n)}</span></> : fmt(n);
  const cards = [
    { key: 'mtd',       label: 'MTD Spend',         value: usdInr(d.mtd_total),                 accent: 'text-emerald-600 bg-emerald-50' },
    { key: 'budget',    label: 'Monthly Budget',    value: d.budget_monthly_usd ? usdInr(d.budget_monthly_usd) : '—', accent: 'text-indigo-600 bg-indigo-50' },
    { key: 'remaining', label: 'Budget Remaining',  value: d.budget_remaining != null ? usdInr(d.budget_remaining) : '—', accent: 'text-blue-600 bg-blue-50' },
    { key: 'pct',       label: 'Budget Used',       value: d.budget_used_pct != null ? `${d.budget_used_pct}%` : '—', accent: 'text-amber-600 bg-amber-50' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-slate-500">
          {d.from_cache ? (
            <>Showing cached data from <b>{d.cached_at ? new Date(d.cached_at).toLocaleTimeString() : ''}</b> · refreshes every 10 min</>
          ) : (
            <>Live data · refreshed just now</>
          )}
        </div>
        <Button
          onClick={forceRefresh}
          disabled={refreshing}
          variant="outline"
          size="sm"
          className="rounded-full h-8 text-xs"
          data-testid={`cost-refresh-${provider}`}
        >
          <RefreshCw className={`w-3 h-3 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Refresh now'}
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`cost-kpi-strip-${provider}`}>
        {cards.map((c) => (
          <div key={c.key} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-start justify-between" data-testid={`cost-kpi-${provider}-${c.key}`}>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{c.label}</div>
              <div className="font-heading text-xl font-bold text-slate-900 mt-1">{c.value}</div>
            </div>
            <div className={`w-9 h-9 rounded-xl ${c.accent} flex items-center justify-center`}>
              <Wallet className="w-4 h-4" strokeWidth={1.8} />
            </div>
          </div>
        ))}
      </div>

      {d.budget_used_pct != null && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex justify-between text-xs text-slate-500 mb-1.5">
            <span>Monthly budget pace</span>
            <span><b>{fmt(d.mtd_total)}</b> of {fmt(d.budget_monthly_usd)}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className={`h-full ${d.budget_used_pct > 100 ? 'bg-rose-500' : d.budget_used_pct > 80 ? 'bg-amber-500' : 'bg-emerald-500'}`}
              style={{ width: `${Math.min(100, d.budget_used_pct)}%` }}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DailyChart series={d.daily_series || []} currency={cur} />
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Top services (MTD)</div>
          {(d.top_services || []).length === 0 && <div className="text-sm text-slate-400">No data</div>}
          {(d.top_services || []).map((s) => (
            <div key={s.service} className="flex justify-between items-center py-2 border-t border-slate-100 first:border-t-0 text-sm">
              <span className="text-slate-700 truncate pr-2">{s.service}</span>
              <span className="font-semibold tabular-nums text-slate-900 text-right whitespace-nowrap">
                {fmt(s.cost)}
                {inrRate ? <span className="text-xs text-emerald-700 font-bold ml-2">{inr(s.cost)}</span> : null}
              </span>
            </div>
          ))}
        </div>
      </div>

      {(d.top_resources || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Top 10 resources (MTD)</div>
            <TrendingUp className="w-4 h-4 text-slate-400" />
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr><th className="text-left px-4 py-2">Resource</th><th className="text-left px-4 py-2">Service</th><th className="text-right px-4 py-2">Cost</th></tr>
            </thead>
            <tbody>
              {(d.top_resources || []).map((r) => (
                <tr key={r.full_id || r.resource} className="border-t border-slate-100">
                  <td className="px-4 py-2.5">
                    <div className="font-semibold text-slate-900">{r.resource}</div>
                    {r.full_id && <div className="text-[10px] text-slate-500 font-mono truncate max-w-md" title={r.full_id}>{r.full_id}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-xs">{r.service}</td>
                  <td className="px-4 py-2.5 text-right font-semibold tabular-nums whitespace-nowrap">
                    {fmt(r.cost)}
                    {inrRate ? <span className="text-xs text-emerald-700 font-bold ml-2">{inr(r.cost)}</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(d.by_account || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Cost by Account (Org)</div>
          {(d.by_account || []).map((a) => (
            <div key={a.account_id} className="flex justify-between items-center py-2 border-t border-slate-100 first:border-t-0 text-sm">
              <span className="text-slate-700 font-mono">{a.account_id}</span>
              <span className="font-semibold tabular-nums text-slate-900 whitespace-nowrap">
                {fmt(a.cost)}
                {inrRate ? <span className="text-xs text-emerald-700 font-bold ml-2">{inr(a.cost)}</span> : null}
              </span>
            </div>
          ))}
        </div>
      )}

      {d.partial_refresh_warning && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="text-xs">{d.partial_refresh_warning}</div>
        </div>
      )}

      {(d.errors || []).length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-sm text-amber-800">
          <div className="font-semibold inline-flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Partial data</div>
          <ul className="mt-1.5 text-xs list-disc list-inside space-y-0.5">
            {d.errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {provider === 'aws' && <AwsTopResourcesPanel inrRate={inrRate} fmt={fmt} inr={inr} bump={bump} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AWS Top Resources by cost (rendered on the Dashboard sub-tab)
// Fetches /aws/cost/inventory once and shows the 20 most expensive resources
// with their Name tag, type, region, state, $ + ₹.
// ---------------------------------------------------------------------------
function AwsTopResourcesPanel({ inrRate, fmt, inr, bump }) {
  const [inv, setInv] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/aws/cost/inventory');
        if (!cancelled) setInv(data);
      } catch (e) {
        if (!cancelled) setInv({ resources: [], error: e?.response?.data?.detail || 'Failed' });
      }
    })();
    return () => { cancelled = true; };
  }, [bump]);

  if (inv === null) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-4">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Resources by cost</div>
        <div className="text-sm text-slate-500 inline-flex items-center gap-2">
          <RefreshCw className="w-3 h-3 animate-spin" /> Scanning your AWS account across all regions… (10–30s on first load)
        </div>
      </div>
    );
  }
  const resources = (inv.resources || [])
    .filter((r) => r.monthly_cost_usd != null && r.monthly_cost_usd > 0)
    .sort((a, b) => (b.monthly_cost_usd || 0) - (a.monthly_cost_usd || 0));
  const top = resources.slice(0, 20);
  const total = resources.reduce((s, r) => s + (r.monthly_cost_usd || 0), 0);

  if (!inv.configured) return null;
  if (resources.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-4">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Resources by cost</div>
        <div className="text-sm text-slate-500">No active resources found in your AWS account.</div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="aws-dashboard-resources">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Top 20 Resources by cost</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {inv.total_count} total resources · Est. monthly <b>{fmt(total)}{inrRate ? <span className="text-emerald-700 ml-1.5">{inr(total)}</span> : null}</b>
            {!inv.resource_level_cost_enabled && <span className="text-amber-700 ml-2 font-semibold">(estimates — enable AWS hourly resource-level data for exact)</span>}
            {inv.resource_level_cost_enabled && <span className="text-emerald-700 ml-2 font-semibold">(AWS-billed exact amounts)</span>}
          </div>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
          <tr>
            <th className="text-left px-3 py-2">Resource</th>
            <th className="text-left px-3 py-2">Service · Type</th>
            <th className="text-left px-3 py-2">Region</th>
            <th className="text-left px-3 py-2">State</th>
            <th className="text-right px-3 py-2">Monthly Cost</th>
          </tr>
        </thead>
        <tbody>
          {top.map((r) => (
            <tr key={r.arn || r.id} className="border-t border-slate-100 hover:bg-slate-50"
                data-testid={`aws-dash-row-${r.id}`}>
              <td className="px-3 py-2.5">
                <div className="font-semibold text-slate-900 text-sm">{r.name || <span className="text-slate-400 italic">(unnamed)</span>}</div>
                <div className="text-[10px] text-slate-500 font-mono truncate max-w-xs">{r.id}</div>
              </td>
              <td className="px-3 py-2.5 text-xs">
                <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-semibold">{r.service}</span>
                <span className="text-slate-500 ml-1.5">{r.instance_type || r.subtype || ''}</span>
              </td>
              <td className="px-3 py-2.5 text-xs text-slate-700 font-mono">{r.region}</td>
              <td className="px-3 py-2.5">
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${
                  ['running', 'available', 'active', 'associated', 'deployed'].includes((r.state || '').toLowerCase())
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : ['stopped', 'unassociated', 'disabled'].includes((r.state || '').toLowerCase())
                    ? 'bg-slate-100 text-slate-600 border-slate-200'
                    : 'bg-amber-50 text-amber-700 border-amber-200'
                }`}>{r.state || '—'}</span>
              </td>
              <td className="px-3 py-2.5 text-right font-semibold tabular-nums whitespace-nowrap">
                {fmt(r.monthly_cost_usd)}
                {inrRate ? <span className="text-xs text-emerald-700 font-bold ml-2">{inr(r.monthly_cost_usd)}</span> : null}
                {r.cost_source === 'estimate' && <div className="text-[9px] text-amber-600 font-normal italic">estimate</div>}
                {r.cost_source === 'ce' && <div className="text-[9px] text-emerald-600 font-normal italic">AWS billed</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {resources.length > 20 && (
        <div className="px-4 py-3 text-center text-xs text-slate-500 border-t border-slate-100">
          Showing top 20 of <b>{resources.length}</b> resources with cost. See the <b>Resources</b> tab above for the full list, filters, and per-resource detail.
        </div>
      )}
    </div>
  );
}

function DailyChart({ series, currency }) {
  if (!series || series.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-4">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Daily spend (last 30 days)</div>
        <div className="text-sm text-slate-400">No daily data</div>
      </div>
    );
  }
  const max = Math.max(...series.map((s) => s.cost), 0.0001);
  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0 }).format(n);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Daily spend (last 30 days)</div>
      <div className="flex items-end gap-0.5 h-32">
        {series.map((s) => (
          <div key={s.date} className="flex-1 flex flex-col items-center justify-end group relative">
            <div className="w-full bg-[#3D4A8E] rounded-t-sm hover:bg-[#2F3A6E]" style={{ height: `${(s.cost / max) * 100}%`, minHeight: '2px' }} />
            <div className="hidden group-hover:block absolute -top-9 bg-slate-900 text-white text-[10px] px-1.5 py-1 rounded whitespace-nowrap z-10">
              {s.date}: {fmt(s.cost)}
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-slate-400 mt-1">
        <span>{series[0]?.date}</span><span>{series[series.length - 1]?.date}</span>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
function ResourcesTab({ cfg, provider, bump }) {
  // AWS gets a rich inventory tab; Entra keeps the original simple list.
  if (provider === 'aws') return <AwsResourcesTab bump={bump} />;
  return <SimpleResourcesTab cfg={cfg} provider={provider} bump={bump} />;
}

function SimpleResourcesTab({ cfg, provider, bump }) {
  const [days, setDays] = useState(30);
  const [filter, setFilter] = useState('');
  const [rows, setRows] = useState(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`${cfg.resources}?days=${days}&limit=500`);
        setRows(data.rows || []); setNote(data.note || '');
      } catch (e) { setRows([]); toast.error(e?.response?.data?.detail || 'Failed'); }
    })();
  }, [days, bump, cfg.resources]);

  const loading = rows === null;
  const displayRows = rows || [];
  const fmt = (n) => `$${(n || 0).toFixed(2)}`;
  const filtered = filter
    ? displayRows.filter((r) =>
        (r.resource || '').toLowerCase().includes(filter.toLowerCase()) ||
        (r.resource_group || '').toLowerCase().includes(filter.toLowerCase()) ||
        (r.service || '').toLowerCase().includes(filter.toLowerCase()))
    : displayRows;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter…" className="rounded-xl max-w-sm" data-testid={`cost-resources-filter-${provider}`} />
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="rounded-xl border border-slate-200 px-3 text-sm h-10" data-testid={`cost-resources-days-${provider}`}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <div className="ml-auto text-xs text-slate-500"><b>{filtered.length}</b> resource(s)</div>
      </div>
      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {note && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">{note}</div>}
      {!loading && displayRows.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Resource</th>
                {provider === 'entra' && <th className="text-left px-4 py-3">Resource Group</th>}
                {provider === 'entra' && <th className="text-left px-4 py-3">Service</th>}
                <th className="text-right px-4 py-3">Cost</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.full_id || r.resource} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5">
                    <div className="font-semibold text-slate-900">{r.resource}</div>
                    {r.full_id && <div className="text-[10px] text-slate-500 font-mono truncate max-w-md" title={r.full_id}>{r.full_id}</div>}
                  </td>
                  {provider === 'entra' && <td className="px-4 py-2.5 text-xs">{r.resource_group}</td>}
                  {provider === 'entra' && <td className="px-4 py-2.5 text-xs">{r.service}</td>}
                  <td className="px-4 py-2.5 text-right font-semibold tabular-nums">{fmt(r.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && displayRows.length === 0 && !note && <EmptyState message="No resource cost data yet." provider={provider} />}
    </div>
  );
}

// ---------- AWS resource INVENTORY tab (rich) ----------
function AwsResourcesTab({ bump }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [running, setRunning] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async (force = false) => {
    if (force) setRefreshing(true);
    try {
      const { data: d } = await api.get(`/aws/cost/inventory${force ? '?force=true' : ''}`);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load inventory');
      setData({ configured: false });
    } finally { setRefreshing(false); }
  };
  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [bump]);

  if (data === null) return <div className="text-sm text-slate-500">Loading resource inventory… (multi-region scan can take 10-30s on first load)</div>;
  if (!data.configured) return <EmptyState message="AWS credentials not configured." provider="aws" />;

  const inrRate = data.inr_rate || 0;
  const fmtUsd = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);
  const fmtInr = (n) => (n == null || !inrRate) ? '' : `₹${(Number(n) * inrRate).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  const fmtBoth = (n) => n == null ? '—' : <><span className="tabular-nums">{fmtUsd(n)}</span><span className="text-[10px] text-slate-500 ml-1">/ {fmtInr(n)}</span></>;

  const resources = data.resources || [];
  const regions = Array.from(new Set(resources.map(r => r.region).filter(Boolean))).sort();
  const services = (data.summary_by_service || []).map(s => s.service);

  let filtered = resources;
  if (serviceFilter) filtered = filtered.filter(r => r.service === serviceFilter);
  if (regionFilter) filtered = filtered.filter(r => r.region === regionFilter);
  if (running) filtered = filtered.filter(r => (r.state || '').toLowerCase() === 'running' || (r.state || '').toLowerCase() === 'available' || (r.state || '').toLowerCase() === 'active');
  if (filter) {
    const q = filter.toLowerCase();
    filtered = filtered.filter(r =>
      (r.name || '').toLowerCase().includes(q) ||
      (r.id || '').toLowerCase().includes(q) ||
      (r.instance_type || '').toLowerCase().includes(q));
  }

  const totalMonthly = filtered.reduce((s, r) => s + (r.monthly_cost_usd || 0), 0);

  return (
    <div className="space-y-3" data-testid="aws-resources-tab">
      {/* Headline KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Resources" value={data.total_count} sub={`${services.length} services · ${regions.length} regions`} />
        <KpiCard label="Filtered View" value={filtered.length} sub={filtered.length === data.total_count ? 'all shown' : 'filtered'} />
        <KpiCard label="Est. Monthly Cost" value={fmtBoth(totalMonthly)} sub="sum of filtered" />
        <KpiCard label="USD → INR" value={inrRate ? `₹${inrRate.toFixed(2)}` : '—'} sub="updated daily" />
      </div>

      {/* Resource-level cost banner */}
      {!data.resource_level_cost_enabled && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 flex items-start gap-2" data-testid="aws-resource-cost-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <b>Per-resource cost data is not enabled.</b> Costs shown below are <b>estimates</b> based on instance type. To see exact per-resource AWS billing, enable
            <a href="https://console.aws.amazon.com/cost-management/home#/settings" target="_blank" rel="noreferrer"
               className="font-bold underline ml-1">Hourly &amp; Resource-level data in Cost Management → Preferences</a>.
            AWS charges a small fee for this (~$0.20/day per million records).
          </div>
        </div>
      )}
      {data.resource_level_cost_enabled && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2.5 text-[11px] text-emerald-900 flex items-center gap-2" data-testid="aws-resource-cost-banner-ok">
          <CheckIconSmall /> Per-resource billing data is enabled — costs below are <b>exact</b> AWS-reported amounts (last 30 days).
        </div>
      )}

      {/* Summary by service */}
      <div className="bg-white border border-slate-200 rounded-2xl p-3" data-testid="aws-summary-by-service">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Cost by Service (30d)</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {(data.summary_by_service || []).map(s => (
            <button key={s.service}
                    onClick={() => setServiceFilter(serviceFilter === s.service ? '' : s.service)}
                    data-testid={`aws-svc-tile-${s.service}`}
                    className={`text-left px-3 py-2 rounded-xl border transition-colors ${
                      serviceFilter === s.service ? 'border-[#3D4A8E] bg-indigo-50' : 'border-slate-200 hover:border-[#3D4A8E]'
                    }`}>
              <div className="text-[11px] font-bold text-slate-800">{s.service}</div>
              <div className="text-[10px] text-slate-500">{s.count} resources</div>
              <div className="text-sm font-bold mt-0.5">
                {s.monthly_cost_usd > 0 ? fmtBoth(s.monthly_cost_usd) : <span className="text-slate-400 text-xs">no cost data</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center bg-white border border-slate-200 rounded-xl p-2">
        <Input value={filter} onChange={(e) => setFilter(e.target.value)}
               placeholder="Search by Name tag / ID / type…"
               className="rounded-xl flex-1 max-w-sm" data-testid="aws-inv-filter" />
        <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}
                data-testid="aws-inv-service"
                className="rounded-xl border border-slate-200 px-3 text-sm h-10 bg-white">
          <option value="">All services</option>
          {services.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)}
                data-testid="aws-inv-region"
                className="rounded-xl border border-slate-200 px-3 text-sm h-10 bg-white">
          <option value="">All regions</option>
          {regions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 text-xs text-slate-600 px-2">
          <input type="checkbox" checked={running} onChange={e => setRunning(e.target.checked)}
                 data-testid="aws-inv-running" />
          Running only
        </label>
        <Button onClick={() => load(true)} size="sm" disabled={refreshing}
                variant="outline" className="rounded-full text-xs ml-auto" data-testid="aws-inv-refresh">
          <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> {refreshing ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      {/* Inventory table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="aws-inv-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2.5">Name</th>
              <th className="text-left px-3 py-2.5">Service</th>
              <th className="text-left px-3 py-2.5">Type</th>
              <th className="text-left px-3 py-2.5">Region</th>
              <th className="text-left px-3 py-2.5">State</th>
              <th className="text-right px-3 py-2.5">Monthly Cost (USD / INR)</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.arn || r.id}
                  onClick={() => setSelected({ ...r, _inr_rate: inrRate })}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  data-testid={`aws-inv-row-${r.id}`}>
                <td className="px-3 py-2">
                  <div className="font-semibold text-slate-900 text-sm">{r.name || <span className="text-slate-400 italic">(unnamed)</span>}</div>
                  <div className="text-[10px] text-slate-500 font-mono truncate max-w-[280px]">{r.id}</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-semibold">{r.service}</span>
                </td>
                <td className="px-3 py-2 text-xs text-slate-700">{r.instance_type || r.subtype || '—'}</td>
                <td className="px-3 py-2 text-xs text-slate-700 font-mono">{r.region || '—'}</td>
                <td className="px-3 py-2"><StateBadge state={r.state} /></td>
                <td className="px-3 py-2 text-right font-semibold">
                  {fmtBoth(r.monthly_cost_usd)}
                  {r.cost_source === 'estimate' && <div className="text-[9px] text-amber-600 font-normal italic">estimate</div>}
                  {r.cost_source === 'ce' && <div className="text-[9px] text-emerald-600 font-normal italic">AWS billed</div>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center text-slate-400 py-10 text-sm">No resources match the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && <AwsResourceDrawer r={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function KpiCard({ label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="text-lg font-bold text-slate-900 mt-0.5">{value ?? '—'}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function StateBadge({ state }) {
  const s = (state || '').toLowerCase();
  const cls = (s === 'running' || s === 'available' || s === 'active' || s === 'associated' || s === 'deployed')
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : (s === 'stopped' || s === 'unassociated' || s === 'disabled' || s === 'inactive')
    ? 'bg-slate-100 text-slate-600 border-slate-200'
    : 'bg-amber-50 text-amber-700 border-amber-200';
  return <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${cls}`}>{state || '—'}</span>;
}

function CheckIconSmall() {
  return <span className="inline-block w-3 h-3 rounded-full bg-emerald-500 text-white text-[8px] flex items-center justify-center">✓</span>;
}

function AwsResourceDrawer({ r, onClose }) {
  const inrRate = r._inr_rate || 0;
  const fmtBoth = (n) => n == null ? '—' : (
    <>${Number(n).toFixed(2)}{inrRate ? ` / ₹${(Number(n) * inrRate).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : ''}</>
  );
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="aws-resource-drawer">
      <div className="flex-1 bg-slate-900/40" onClick={onClose} />
      <div className="w-full max-w-xl bg-white border-l border-slate-200 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-4 z-10">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">{r.service} · {r.subtype || ''}</div>
          <div className="font-heading text-lg font-bold mt-0.5">{r.name || r.id}</div>
          <div className="text-[11px] text-slate-500 font-mono break-all mt-0.5">{r.arn || r.id}</div>
          <div className="flex gap-2 mt-2 flex-wrap">
            <StateBadge state={r.state} />
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{r.region}</span>
            {r.instance_type && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">{r.instance_type}</span>}
          </div>
        </div>
        <div className="p-4 space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 text-sm space-y-1">
            <KvRow k="Monthly cost" v={<><b>{fmtBoth(r.monthly_cost_usd)}</b>{r.cost_source === 'estimate' && <span className="text-[10px] text-amber-600 ml-1">(estimate)</span>}{r.cost_source === 'ce' && <span className="text-[10px] text-emerald-600 ml-1">(AWS billed)</span>}</>} />
            <KvRow k="Resource ID" v={r.id} mono />
            <KvRow k="Created" v={r.created_at ? new Date(r.created_at).toLocaleString() : '—'} />
            {r.metadata && Object.entries(r.metadata).map(([k, v]) => (
              v !== null && v !== undefined && v !== '' && <KvRow key={k} k={k.replace(/_/g, ' ')} v={Array.isArray(v) ? v.join(', ') : (typeof v === 'object' ? JSON.stringify(v) : String(v))} mono={k.includes('id') || k.includes('ip')} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function KvRow({ k, v, mono }) {
  return (
    <div className="flex justify-between gap-3 py-1 border-b border-slate-100 last:border-0">
      <span className="text-slate-500 capitalize">{k}</span>
      <span className={`text-slate-900 font-medium text-right break-all ${mono ? 'font-mono text-[11px]' : ''}`}>{v ?? '—'}</span>
    </div>
  );
}

// --------------------------------------------------------------------------
function ReportsTab({ cfg, provider }) {
  const [reports, setReports] = useState([]);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(cfg.reportsList); setReports(data || []); }
      catch { setReports([]); }
    })();
  }, [cfg.reportsList]);
  const token = localStorage.getItem('hrms_token');
  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">Download cost reports for finance, budget approvals, and compliance.</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {reports.map((r) => (
          <div key={r.kind} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`cost-report-${provider}-${r.kind}`}>
            <div className="font-semibold text-slate-900 text-sm">{r.label}</div>
            <div className="text-[11px] text-slate-500 font-mono mt-0.5">{r.kind}</div>
            <div className="flex gap-1.5 mt-3 flex-wrap">
              <a href={cfg.reportPath(r.kind, 'pdf', token)} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100" data-testid={`cost-report-pdf-${provider}-${r.kind}`}>
                <FileText className="w-3 h-3" /> PDF
              </a>
              <a href={cfg.reportPath(r.kind, 'xlsx', token)} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100" data-testid={`cost-report-xlsx-${provider}-${r.kind}`}>
                <FileSpreadsheet className="w-3 h-3" /> Excel
              </a>
              <a href={cfg.reportPath(r.kind, 'csv', token)} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100" data-testid={`cost-report-csv-${provider}-${r.kind}`}>
                <Download className="w-3 h-3" /> CSV
              </a>
            </div>
          </div>
        ))}
        {reports.length === 0 && <div className="text-sm text-slate-400">No reports.</div>}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Settings panel for Cost Management (Entra/Azure variant only — AWS uses
// the main AWS Settings page which already covers creds + budget).
// --------------------------------------------------------------------------
function SettingsTab({ cfg, provider, onSaved }) {
  const [info, setInfo] = useState({ subscription_ids: [], currency: 'USD',
    budget_monthly_usd: '', entra_credentials_set: false });
  const [busy, setBusy] = useState('');
  const [newSub, setNewSub] = useState('');
  const [discovered, setDiscovered] = useState([]);

  useEffect(() => {
    if (provider !== 'entra') return;
    (async () => {
      try { const { data } = await api.get(cfg.settings); setInfo(data); }
      catch { /* ignore */ }
    })();
  }, [cfg.settings, provider]);

  if (provider === 'aws') {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 max-w-2xl">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">AWS Cost Management Settings</div>
        <p className="text-sm text-slate-600">
          AWS cost data uses the same credentials as the AWS module. Configure your AWS Access Key,
          Secret, Region, and Organization mode in the <b>AWS Settings</b> tab (top-level). The monthly
          budget defined there drives the dashboard&apos;s &ldquo;Budget Remaining&rdquo; &amp; &ldquo;% Used&rdquo; widgets.
        </p>
      </div>
    );
  }

  const save = async () => {
    setBusy('save');
    try {
      const payload = {
        subscription_ids: info.subscription_ids,
        currency: info.currency,
        budget_monthly_usd: info.budget_monthly_usd ? Number(info.budget_monthly_usd) : 0,
      };
      await api.put(cfg.settings, payload);
      toast.success('FinOps settings saved');
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(''); }
  };

  const testConn = async () => {
    setBusy('test');
    try {
      const { data } = await api.post(cfg.testConn);
      if (data.ok) toast.success(`Connected. ${(data.accessible || []).length} subscription(s) accessible.`);
      else toast.error(data.detail || 'Test failed');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Test failed'); }
    finally { setBusy(''); }
  };

  const discover = async () => {
    setBusy('discover');
    try {
      const { data } = await api.get(cfg.subscriptions);
      setDiscovered(data || []);
      if ((data || []).length === 0)
        toast.message('No subscriptions visible. Grant "Cost Management Reader" RBAC.');
      else toast.success(`Discovered ${data.length} subscription(s).`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Discovery failed'); }
    finally { setBusy(''); }
  };

  const addSub = (subId) => {
    const s = (subId || newSub).trim();
    if (!s) return;
    if (info.subscription_ids.includes(s)) { toast.message('Already added'); return; }
    setInfo({ ...info, subscription_ids: [...info.subscription_ids, s] });
    setNewSub('');
  };
  const removeSub = (s) => setInfo({ ...info, subscription_ids: info.subscription_ids.filter((x) => x !== s) });

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-3xl space-y-4">
      <div>
        <label className="text-xs font-semibold text-slate-600">{cfg.labels.pickerLabel}</label>
        <div className="text-[11px] text-slate-500 mb-2">{cfg.labels.pickerHint}</div>
        <div className="flex gap-2">
          <Input value={newSub} onChange={(e) => setNewSub(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSub())}
                 placeholder={cfg.labels.addPlaceholder} className="rounded-xl font-mono"
                 data-testid="cost-new-sub-input" />
          <Button onClick={() => addSub()} className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="cost-add-sub-btn">Add</Button>
          <Button onClick={discover} disabled={busy === 'discover'} variant="outline" className="rounded-full" data-testid="cost-discover-btn">
            <Cloud className={`w-3.5 h-3.5 mr-1.5 ${busy === 'discover' ? 'animate-spin' : ''}`} /> Discover
          </Button>
        </div>
        {discovered.length > 0 && (
          <div className="mt-3 border border-dashed border-slate-300 rounded-xl p-3 bg-slate-50">
            <div className="text-[11px] font-semibold text-slate-500 mb-2">Discovered — click to add</div>
            <div className="flex flex-wrap gap-1.5">
              {discovered.map((d) => (
                <button key={d.id} onClick={() => addSub(d.id)}
                        className="text-xs px-2.5 py-1 rounded-full bg-white border border-slate-200 hover:bg-indigo-50 hover:border-indigo-200">
                  {d.name} <span className="text-slate-400 font-mono ml-1">{d.id?.slice(0, 8)}…</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="cost-sub-chips">
          {info.subscription_ids.map((s) => (
            <span key={s} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-mono">
              {s}
              <button onClick={() => removeSub(s)} className="hover:text-rose-600">×</button>
            </span>
          ))}
          {info.subscription_ids.length === 0 && <span className="text-xs text-slate-400">None yet</span>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
        <div>
          <label className="text-xs font-semibold text-slate-600">Display currency</label>
          <select value={info.currency} onChange={(e) => setInfo({ ...info, currency: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 mt-1 text-sm" data-testid="cost-currency">
            <option value="USD">USD</option><option value="INR">INR</option><option value="EUR">EUR</option><option value="GBP">GBP</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Monthly budget (USD)</label>
          <Input type="number" min={0} step={100} value={info.budget_monthly_usd || ''}
                 onChange={(e) => setInfo({ ...info, budget_monthly_usd: e.target.value })}
                 placeholder="e.g., 5000" className="rounded-xl mt-1" data-testid="cost-budget" />
        </div>
      </div>

      <div className="flex gap-2 flex-wrap pt-2 border-t border-slate-100">
        <Button onClick={save} disabled={busy === 'save'} className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="cost-save-btn">
          {busy === 'save' ? 'Saving…' : 'Save settings'}
        </Button>
        <Button onClick={testConn} disabled={busy === 'test' || info.subscription_ids.length === 0}
                variant="outline" className="rounded-full" data-testid="cost-test-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy === 'test' ? 'animate-spin' : ''}`} />
          Test connection
        </Button>
      </div>

      <GrantAccessWizard
        clientId={info.entra_client_id}
        tenantId={info.entra_tenant_id}
        subscriptionIds={info.subscription_ids}
      />
    </div>
  );
}

// --------------------------------------------------------------------------
// "Grant Access" wizard — turns the 4-click-per-sub Azure portal flow into
// a single Azure CLI / PowerShell command pasted into Cloud Shell.
// --------------------------------------------------------------------------
function GrantAccessWizard({ clientId, tenantId, subscriptionIds }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('cli');

  if (!clientId || (subscriptionIds || []).length === 0) return null;

  const subsList = subscriptionIds.map((s) => `"${s}"`).join(' ');
  const psSubsList = subscriptionIds.map((s) => `"${s}"`).join(',');

  const cliScript = `# 1) Sign in (skip if already authed in Cloud Shell)
az login --tenant ${tenantId}

# 2) Resolve the Service Principal object id for your Entra App
SP_OBJECT_ID=$(az ad sp show --id ${clientId} --query id -o tsv)
echo "Service Principal Object ID: $SP_OBJECT_ID"

# 3) Grant 'Cost Management Reader' on each subscription
for SUB in ${subsList}; do
  echo "Granting on $SUB …"
  az role assignment create \\
    --assignee-object-id "$SP_OBJECT_ID" \\
    --assignee-principal-type ServicePrincipal \\
    --role "Cost Management Reader" \\
    --scope "/subscriptions/$SUB"
done

echo "Done. Click 'Refresh now' on the HRMS Cost Management dashboard."`;

  const psScript = `# 1) Connect (skip if already authed in Cloud Shell)
Connect-AzAccount -TenantId "${tenantId}"

# 2) Resolve the Service Principal object id
$SP = Get-AzADServicePrincipal -ApplicationId "${clientId}"
Write-Host "Service Principal Object ID: $($SP.Id)"

# 3) Grant 'Cost Management Reader' on each subscription
$Subscriptions = @(${psSubsList})
foreach ($Sub in $Subscriptions) {
  Write-Host "Granting on $Sub …"
  New-AzRoleAssignment \`
    -ObjectId $SP.Id \`
    -RoleDefinitionName "Cost Management Reader" \`
    -Scope "/subscriptions/$Sub"
}

Write-Host "Done. Click 'Refresh now' on the HRMS Cost Management dashboard."`;

  const portalUrl = `https://shell.azure.com/${tab === 'cli' ? 'bash' : 'powershell'}`;
  const script = tab === 'cli' ? cliScript : psScript;

  const copy = async () => {
    // Try modern Clipboard API first
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(script);
        toast.success('Copied to clipboard. Paste into Azure Cloud Shell.');
        return;
      }
    } catch { /* fall through to legacy path */ }
    // Legacy fallback — works inside cross-origin iframes (preview environments)
    try {
      const ta = document.createElement('textarea');
      ta.value = script;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.setAttribute('readonly', '');
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, script.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) {
        toast.success('Copied to clipboard. Paste into Azure Cloud Shell.');
        return;
      }
    } catch { /* both methods failed */ }
    toast.error('Browser blocked auto-copy. Click the script area and use Ctrl+A → Ctrl+C.');
  };

  return (
    <div className="border-t border-slate-100 pt-4 mt-2" data-testid="cost-grant-access-wizard">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left flex items-center justify-between gap-2 hover:opacity-80"
        data-testid="cost-grant-access-toggle"
      >
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#3D4A8E] inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Grant Access Wizard
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Generate a one-shot Azure CLI / PowerShell command to grant <b>Cost Management Reader</b> on all {subscriptionIds.length} subscription(s) below.
          </div>
        </div>
        <span className="text-xs text-slate-400">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="mt-4 space-y-3">
          <div className="flex gap-1 border border-slate-200 rounded-full p-1 bg-slate-50 w-fit" data-testid="cost-grant-tabs">
            <button
              onClick={() => setTab('cli')}
              className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${tab === 'cli' ? 'bg-[#3D4A8E] text-white' : 'text-slate-600'}`}
              data-testid="cost-grant-tab-cli"
            >
              Azure CLI (bash)
            </button>
            <button
              onClick={() => setTab('ps')}
              className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${tab === 'ps' ? 'bg-[#3D4A8E] text-white' : 'text-slate-600'}`}
              data-testid="cost-grant-tab-ps"
            >
              PowerShell
            </button>
          </div>

          <div className="relative">
            <pre
              className="bg-slate-900 text-slate-100 text-[11px] font-mono p-4 rounded-2xl overflow-x-auto leading-relaxed cursor-text select-all"
              data-testid="cost-grant-script"
              onClick={(e) => {
                // Triple-click selects all in <pre> — this helper makes a single
                // click select the entire block so users can hit Ctrl+C straight away
                const range = document.createRange();
                range.selectNodeContents(e.currentTarget);
                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(range);
              }}
            >
              {script}
            </pre>
            <Button
              onClick={copy}
              size="sm"
              className="absolute top-2 right-2 rounded-full bg-slate-700 hover:bg-slate-600 text-white h-7 px-3 text-[11px]"
              data-testid="cost-grant-copy"
            >
              <Copy className="w-3 h-3 mr-1" /> Copy
            </Button>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-xs text-indigo-900">
            <div className="font-semibold mb-1">How to use:</div>
            <ol className="list-decimal list-inside space-y-0.5 text-[11px]">
              <li>Click <b>Copy</b> above.</li>
              <li>Open <a href={portalUrl} target="_blank" rel="noreferrer" className="underline font-semibold" data-testid="cost-grant-cloud-shell-link">Azure Cloud Shell</a> ({tab === 'cli' ? 'bash' : 'PowerShell'} mode).</li>
              <li>Paste the script and press Enter. It will iterate through every subscription.</li>
              <li>Come back here and click <b>Refresh now</b> on the Dashboard tab — the 403s should disappear.</li>
            </ol>
            <div className="mt-2 text-[10px] text-indigo-700">
              <b>Note:</b> The user running these commands needs <code>Owner</code> or <code>User Access Administrator</code> on each subscription to grant new role assignments.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState({ message, provider, throttled }) {
  return (
    <div className={`border rounded-2xl p-10 text-center ${throttled ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
      {throttled ? <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" strokeWidth={1.5} />
                  : <Cloud className="w-10 h-10 text-slate-300 mx-auto" strokeWidth={1.5} />}
      <div className="text-slate-700 font-semibold mt-3">{message}</div>
      <div className="text-xs text-slate-500 mt-1">
        {throttled
          ? <>Azure Cost Management has temporary throttling. Wait a minute and try the <b>Refresh now</b> button. We&apos;ll cache the next successful response for 10 minutes so you don&apos;t hit the limit again.</>
          : <>Open <b>Settings</b> to configure {provider === 'entra' ? 'Azure subscription IDs' : 'AWS credentials'} and verify the required cost-reader role / policy is granted.</>}
      </div>
    </div>
  );
}
